// src/errors/api-error.ts
var API_ERROR_CONFIG = {
  BAD_REQUEST: {
    code: "BAD_REQUEST",
    message: "Bad request"
  },
  CONFLICT: {
    code: "CONFLICT",
    message: "Conflict with existing data"
  },
  NETWORK_ERROR: {
    code: "NETWORK_ERROR",
    message: "Network error occurred"
  },
  NOT_FOUND: {
    code: "NOT_FOUND",
    message: "Resource not found"
  },
  RATE_LIMITED: {
    code: "RATE_LIMITED",
    message: "Too many requests"
  },
  SERVER_ERROR: {
    code: "SERVER_ERROR",
    message: "Internal server error"
  },
  TEAPOT: {
    code: "TEAPOT",
    message: "Server is a teapot"
  },
  FORBIDDEN: {
    code: "FORBIDDEN",
    message: "Forbidden"
  },
  UNAUTHORIZED: {
    code: "UNAUTHORIZED",
    message: "Unauthorized"
  },
  UNKNOWN_ERROR: {
    code: "UNKNOWN_ERROR",
    message: "Unknown error occurred"
  },
  INVALID_ERROR: {
    code: "INVALID_ERROR",
    message: "An invalid error was thrown"
  },
  REQUEST_CANCELLED: {
    code: "REQUEST_CANCELLED",
    message: "Request was cancelled"
  }
};
var ApiError = class _ApiError extends Error {
  constructor(message, details) {
    super(message);
    this.details = details;
    this.name = "ApiError";
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, _ApiError);
    }
  }
};
function createApiError(message, error, code, status) {
  const details = {
    code,
    status,
    originalError: error,
    response: error.response ? {
      data: error.response.data,
      headers: error.response.headers
    } : void 0,
    request: error.config ? {
      url: error.config.url,
      method: error.config.method,
      headers: error.config.headers,
      data: error.config.data
    } : void 0
  };
  return new ApiError(message, details);
}
function handleApiError(error) {
  if (!error || typeof error !== "object") {
    return Promise.reject(
      createApiError(API_ERROR_CONFIG.INVALID_ERROR.message, error, API_ERROR_CONFIG.INVALID_ERROR.code)
    );
  }
  if ("__CANCEL__" in error) {
    return Promise.reject(
      createApiError(API_ERROR_CONFIG.REQUEST_CANCELLED.message, error, API_ERROR_CONFIG.REQUEST_CANCELLED.code)
    );
  }
  if (!("response" in error)) {
    return Promise.reject(
      createApiError(API_ERROR_CONFIG.NETWORK_ERROR.message, error, API_ERROR_CONFIG.NETWORK_ERROR.code)
    );
  }
  if (typeof error === "object" && "status" in error && typeof error.status === "number") {
    switch (error.status) {
      case 400:
        return Promise.reject(
          createApiError(API_ERROR_CONFIG.BAD_REQUEST.message, error, API_ERROR_CONFIG.BAD_REQUEST.code, error.status)
        );
      case 401:
        return Promise.reject(
          createApiError(
            API_ERROR_CONFIG.UNAUTHORIZED.message,
            error,
            API_ERROR_CONFIG.UNAUTHORIZED.code,
            error.status
          )
        );
      case 403:
        return Promise.reject(
          createApiError(API_ERROR_CONFIG.FORBIDDEN.message, error, API_ERROR_CONFIG.FORBIDDEN.code, error.status)
        );
      case 404:
        return Promise.reject(
          createApiError(API_ERROR_CONFIG.NOT_FOUND.message, error, API_ERROR_CONFIG.NOT_FOUND.code, error.status)
        );
      case 409:
        return Promise.reject(
          createApiError(API_ERROR_CONFIG.CONFLICT.message, error, API_ERROR_CONFIG.CONFLICT.code, error.status)
        );
      case 418:
        return Promise.reject(
          createApiError(API_ERROR_CONFIG.TEAPOT.message, error, API_ERROR_CONFIG.TEAPOT.code, error.status)
        );
      case 429:
        return Promise.reject(
          createApiError(
            API_ERROR_CONFIG.RATE_LIMITED.message,
            error,
            API_ERROR_CONFIG.RATE_LIMITED.code,
            error.status
          )
        );
      case 500:
        return Promise.reject(
          createApiError(
            API_ERROR_CONFIG.SERVER_ERROR.message,
            error,
            API_ERROR_CONFIG.SERVER_ERROR.code,
            error.status
          )
        );
      default:
        return Promise.reject(
          createApiError(
            API_ERROR_CONFIG.UNKNOWN_ERROR.message,
            error,
            API_ERROR_CONFIG.UNKNOWN_ERROR.code,
            error.status
          )
        );
    }
  }
  return Promise.reject(
    createApiError(API_ERROR_CONFIG.UNKNOWN_ERROR.message, error, API_ERROR_CONFIG.UNKNOWN_ERROR.code)
  );
}
function shouldSignOutOnError(error) {
  if (isApiError(error) && error.details.code === API_ERROR_CONFIG.UNAUTHORIZED.code) {
    if (error.details.status === 401) {
      return true;
    }
  }
  if (error && typeof error === "object" && "data" in error && error.data && typeof error.data === "object" && "code" in error.data && error.data.code === "UNAUTHORIZED" && "httpStatus" in error.data && error.data.httpStatus === 401) {
    return true;
  }
  return false;
}
function isApiError(error) {
  return error instanceof ApiError;
}
var DEFAULT_MESSAGE_CONFIG = {
  BAD_REQUEST: "Please check the input and try again.",
  CONFLICT: "Data changed elsewhere. Refresh to get the latest.",
  NETWORK_ERROR: "Network connection lost. Please reconnect and retry.",
  NOT_FOUND: "Can't find that, try searching or heading back.",
  RATE_LIMITED: "Too many requests. Please wait a moment.",
  SERVER_ERROR: "We hit a snag. Please refresh or try again.",
  TEAPOT: "I'm a teapot! (Kidding, let's try something else.)",
  UNAUTHORIZED: "Please sign back in.",
  FORBIDDEN: "No access here, check permissions.",
  UNKNOWN_ERROR: "Something unexpected happened. Try again or contact support.",
  INVALID_ERROR: "Something unexpected happened. Try again or contact support.",
  REQUEST_CANCELLED: "Request cancelled. Try starting over if this wasn't expected."
};
function getApiErrorDescription(apiError, config) {
  const { code } = apiError.details;
  if (config && code in config) {
    return config[code];
  }
  return DEFAULT_MESSAGE_CONFIG[code] || DEFAULT_MESSAGE_CONFIG["UNKNOWN_ERROR"];
}
var NO_API_RETRY_CASES = [
  "NOT_FOUND",
  "NETWORK_ERROR",
  "BAD_REQUEST",
  "RATE_LIMITED",
  "TEAPOT",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "CONFLICT"
];
var NO_TRPC_RETRY_CASES = [
  "NOT_FOUND",
  "BAD_REQUEST",
  "FORBIDDEN",
  "UNAUTHORIZED",
  "CONFLICT",
  "TOO_MANY_REQUESTS"
];
var DEFAULT_TRPC_MESSAGE_CONFIG = {
  BAD_REQUEST: "Please check the input and try again.",
  UNAUTHORIZED: "Please sign back in.",
  FORBIDDEN: "No access here, check permissions.",
  NOT_FOUND: "Can't find that, try searching or heading back.",
  TOO_MANY_REQUESTS: "Too many requests. Please wait a moment.",
  INTERNAL_SERVER_ERROR: "We hit a snag. Please refresh or try again.",
  CONFLICT: "Data changed elsewhere. Refresh to get the latest.",
  TIMEOUT: "Request took too long. Please try again."
};
function getTRPCErrorDescription(code, config) {
  if (code && code in DEFAULT_TRPC_MESSAGE_CONFIG) {
    const typedCode = code;
    if (config && typedCode in config) {
      return config[typedCode] ?? DEFAULT_TRPC_MESSAGE_CONFIG[typedCode];
    }
    return DEFAULT_TRPC_MESSAGE_CONFIG[typedCode];
  }
  return DEFAULT_MESSAGE_CONFIG.UNKNOWN_ERROR;
}

// src/errors/invitation-errors.ts
var INVITATION_ERROR_CODES = {
  USER_NOT_FOUND: "USER_NOT_FOUND",
  ACCOUNT_ALREADY_ACTIVE: "ACCOUNT_ALREADY_ACTIVE",
  EMAIL_ALREADY_ACTIVE: "EMAIL_ALREADY_ACTIVE"
};
var INVITATION_ERROR_MESSAGES = {
  USER_NOT_FOUND: "User not found",
  ACCOUNT_ALREADY_ACTIVE: "Account is already active",
  EMAIL_ALREADY_ACTIVE: "An active account already exists for this email"
};

// src/errors/setup-errors.ts
var SETUP_ERROR_CODES = {
  SETUP_FAILED: "SETUP_FAILED",
  EMAIL_REQUIRED: "EMAIL_REQUIRED",
  INVITE_INVALID: "INVITE_INVALID",
  INVITE_EXPIRED: "INVITE_EXPIRED",
  OTP_INVALID: "OTP_INVALID",
  OTP_EXPIRED: "OTP_EXPIRED",
  OTP_RATE_LIMITED: "OTP_RATE_LIMITED"
};
var SETUP_ERROR_MESSAGES = {
  SETUP_FAILED: "We hit a snag. Please refresh or try again.",
  EMAIL_REQUIRED: "Email is required.",
  INVITE_INVALID: "This invite link isn't valid.",
  INVITE_EXPIRED: "This invite has expired.",
  OTP_INVALID: "That code doesn't match. Please try again.",
  OTP_EXPIRED: "That code expired. Request a new one.",
  OTP_RATE_LIMITED: "Too many attempts. Wait a moment, then try again."
};
export {
  API_ERROR_CONFIG,
  ApiError,
  DEFAULT_MESSAGE_CONFIG,
  DEFAULT_TRPC_MESSAGE_CONFIG,
  INVITATION_ERROR_CODES,
  INVITATION_ERROR_MESSAGES,
  NO_API_RETRY_CASES,
  NO_TRPC_RETRY_CASES,
  SETUP_ERROR_CODES,
  SETUP_ERROR_MESSAGES,
  createApiError,
  getApiErrorDescription,
  getTRPCErrorDescription,
  handleApiError,
  isApiError,
  shouldSignOutOnError
};
//# sourceMappingURL=index.js.map